// "Edit contact (all details)" for a quotation that is already saved. Saving
// the contact also brings its address, mobile, email and GSTIN onto this
// document (client, 2026-10-24: "edit contact hai but uske sath sath address
// mobile numbers jo automatic sync ho").
import { useState } from 'react';
import { Edit2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { ContactFormModal } from '@/features/contacts/components/ContactFormModal';
import { useContact, useContactMutations } from '@/features/contacts/hooks/useContacts';
import type { UpdateContactPayload, UpdateContactExtendedPayload, CreateContactPayload } from '@/features/contacts/types';

export function EditContactButton({ contactId, onSaved, className }: { contactId: string; onSaved: () => void | Promise<void>; className?: string }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const queryClient = useQueryClient();
  const { data: fullContact } = useContact(open ? contactId : '');
  const { updateContact, updateContactExtended } = useContactMutations();

  const submit = async (basic: CreateContactPayload | UpdateContactPayload, extended: UpdateContactExtendedPayload) => {
    try {
      setSaving(true);
      await updateContact.mutateAsync({ id: contactId, payload: basic as UpdateContactPayload });
      if (Object.keys(extended).length > 0) await updateContactExtended.mutateAsync({ id: contactId, payload: extended });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['contacts'] }),
        queryClient.invalidateQueries({ queryKey: ['contacts', 'detail', contactId] }),
        queryClient.invalidateQueries({ queryKey: ['quotations', 'bill-to-preview', contactId] }),
        queryClient.invalidateQueries({ queryKey: ['sales', 'bill-to', contactId] }),
      ]);
      await onSaved();
      setOpen(false);
    } catch (err) {
      const e = err as { response?: { data?: { error?: string } }; message?: string };
      toast.error(e?.response?.data?.error || e?.message || 'Failed to save contact.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className={className || 'inline-flex items-center gap-1 text-body-xs font-semibold text-primary hover:underline'}>
        <Edit2 className="h-3 w-3" /> Edit contact (all details)
      </button>
      <ContactFormModal
        open={open && !!fullContact}
        contact={(fullContact as never) ?? null}
        loading={saving}
        onClose={() => setOpen(false)}
        onSubmit={submit}
      />
    </>
  );
}

export default EditContactButton;
